import React, { useEffect, useState } from 'react';

export type NotificationType = 'success' | 'error' | 'warning' | 'info';

interface NotificationModalProps {
  isOpen: boolean;
  type: NotificationType;
  title?: string;
  message: string;
  duration?: number;
  onClose: () => void;
  closeLabel?: string;
}

const NotificationModal: React.FC<NotificationModalProps> = ({
  isOpen,
  type,
  title,
  message,
  duration = 3000,
  onClose,
  closeLabel = 'Закрыть'
}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      // Errors stay open until manually dismissed
      if (type === 'error') return;
      const timer = setTimeout(() => {
        setIsVisible(false);
        setTimeout(onClose, 300);
      }, duration);

      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
    }
  }, [isOpen, type, duration, onClose]);

  if (!isOpen) return null;

  const getTypeStyles = () => {
    switch (type) {
      case 'success':
        return {
          bg: 'bg-green-900/90',
          border: 'border-green-500',
          icon: '✅',
          titleColor: 'text-green-200'
        };
      case 'error':
        return {
          bg: 'bg-red-900/90',
          border: 'border-red-500',
          icon: '❌',
          titleColor: 'text-red-200'
        };
      case 'warning':
        return {
          bg: 'bg-orange-900/90',
          border: 'border-orange-500',
          icon: '⚠️',
          titleColor: 'text-orange-200'
        };
      case 'info':
        return {
          bg: 'bg-purple-900/90',
          border: 'border-purple-500',
          icon: 'ℹ️',
          titleColor: 'text-purple-200'
        };
      default:
        return {
          bg: 'bg-gray-900/90',
          border: 'border-gray-500',
          icon: 'ℹ️',
          titleColor: 'text-gray-200'
        };
    }
  };

  const styles = getTypeStyles();

  const handleClick = () => {
    setIsVisible(false);
    setTimeout(onClose, 300);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape' || e.key === 'Enter') {
      handleClick();
    }
  };

  return (
    <div
      className={`fixed inset-0 z-[100000] flex items-center justify-center p-4 transition-all duration-300 ${
        isVisible ? 'opacity-100' : 'opacity-0'
      }`}
      onClick={handleClick}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

      {/* Modal Content */}
      <div
        className={`relative ${styles.bg} border-2 ${styles.border} rounded-lg p-6 max-w-md w-full shadow-2xl transition-all duration-300 ${
          isVisible ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
        tabIndex={0}
        role="alert"
      >
        <div className="flex items-start gap-4">
          <span className="text-3xl flex-shrink-0">{styles.icon}</span>
          <div className="flex-1 min-w-0">
            {title && (
              <h4 className={`font-bold font-gaming text-lg ${styles.titleColor} mb-2`}>
                {title}
              </h4>
            )}
            <p className="text-white text-sm leading-relaxed">{message}</p>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleClick();
            }}
            className="text-gray-400 hover:text-white transition-colors flex-shrink-0 ml-2 text-xl"
            aria-label={closeLabel}
          >
            ✕
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationModal;